import React, { useState } from 'react';
import { Box, Divider, Backdrop } from '@mui/material';
import { useForm, FormProvider, FieldErrors } from 'react-hook-form';
import Spinner from '../common/Spinner';
import ErrorAlert from '../common/ErrorAlert';
import InfoAlert from '../common/InfoAlert';
import { analyticsService } from '../../services/analyticsService';
import { MedicWorkloadDto } from '../../models/analytics/medicWorkloadDto';
import { MedicWorkloadFilterBar, MedicWorkloadFilterForm } from './MedicWorkloadFilterBar';
import { MedicWorkloadDataTable } from './MedicWorkloadDataTable';
import { DateUtils } from '../../helpers/dateUtils';

export const MedicWorkload: React.FC = () => {
    const [data, setData] = useState<MedicWorkloadDto | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(null);

    const methods = useForm<MedicWorkloadFilterForm>({
        defaultValues: {
            period: '',
            date: null,
            interval: '',
            medics: [],
        }
    });

    const onSubmit = (formData: MedicWorkloadFilterForm) => {
        setLoading(true);
        setError(null);
        setInfo(null);

        if (!formData.period || !formData.date || !formData.interval) {
            setError('Не все поля заполнены');
            setLoading(false);
            return;
        }

        // Enum values are already latin strings (enum names), so we can send them directly
        analyticsService.getMedicWorkload({
            period: formData.period,
            date: formData.date,
            interval: formData.interval,
            medicIds: formData.medics.map(m => m.id),
            timeZoneOffset: DateUtils.getTimeZoneShiftHours(),
        })
            .then(r => {
                setData(r.data.data);
                if (r.data.data.medics.length === 0) {
                    setInfo('Данные за выбранный период отсутствуют');
                } else {
                    setInfo(null);
                }
            })
            .catch(e => {
                const errorMessage = e.response?.data?.message || e.message || 'Ошибка при получении данных';
                setError(errorMessage);
                if (errorMessage.includes('отсутствуют')) {
                    setInfo('Данные за выбранный период отсутствуют');
                }
            })
            .finally(() => setLoading(false));
    };

    const onSubmitError = (errors: FieldErrors<MedicWorkloadFilterForm>) => {
        setError('Исправьте ошибки ввода');
    };

    return (
        <Box mt={3}>
            <FormProvider {...methods}>
                <MedicWorkloadFilterBar
                    methods={methods}
                    onSubmit={onSubmit}
                    onSubmitError={onSubmitError}
                />
            </FormProvider>

            {error && (
                <ErrorAlert
                    message={error}
                    onClose={() => setError('')}
                />
            )}

            {info && (
                <InfoAlert
                    message={info}
                    severity="info"
                    onClose={() => setInfo('')}
                    autoCloseDuration={8000}
                />
            )}

            <Divider sx={{ my: 2, borderColor: '#e0e0e0', borderWidth: 1 }} />

            {data && data.medics.length > 0 && (
                <MedicWorkloadDataTable data={data} />
            )}

            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
                open={loading}
            >
                <Spinner />
            </Backdrop>
        </Box>
    );
};

